import { DataNode } from 'components/elements/Form/select/NestedAntdMultiSelect';

/**
 * The function returns an error message for a given entity name when an API fails to fetch data.
 */
export const generateApiFailErrorMessage = (entityName: string): string => {
  return `Something went wrong while fetching ${entityName} data!`;
};

/**
 * The function generates a success message to indicate that an entity has been saved successfully.
 */
export const generateSaveSuccessMessage = (entityName: string): string => {
  return `${entityName} saved successfully!`;
};

/**
 * The function generates an error message to indicate that an entity failed to get saved.
 */
export const generateSaveErrorMessage = (entityName: string): string => {
  return `Failed to save ${entityName} details. Please try again!`;
};

/**
 * The function generates a success message to indicate that an entity has been updated successfully.
 */
export const generateUpdateSuccessMessage = (entityName: string): string => {
  return `${entityName} updated successfully!`;
};

/**
 * The function generates an error message to indicate that an entity failed to get updated.
 */
export const generateUpdateErrorMessage = (entityName: string): string => {
  return `Failed to update ${entityName} details. Please try again!`;
};

/**
 * The function generates a success message to indicate that an entity has been deleted successfully.
 */
export const generateDeleteSuccessMessage = (entityName: string): string => {
  return `${entityName} deleted successfully!`;
};

/**
 * The function generates an error message to indicate that an entity failed to get deleted.
 */
export const generateDeleteErrorMessage = (entityName: string): string => {
  return `Failed to delete ${entityName}. Please try again!`;
};

/**
 * The function generates an error message to indicate that the broadcast message was not sent successfully.
 */
export const generateBroadcastErrorMessage = () => {
  return `Failed to send the Message. Please try again!`;
};

/**
 * The function generates a success message to indicate that the broadcast message was sent successfully.
 */
export const generateBroadcastSuccessMessage = () => {
  return `Message Broadcasted Successfully!`;
};

/**
 * The sorting function `sortDataNodesByTitle` sorts an array of `DataNode` objects by extracting numeric parts
 * from their `title` property for comparison.
 */
export const sortDataNodesByTitle = (nodes: DataNode[] | undefined): DataNode[] | undefined => {
  // Extract numeric parts for comparison
  const regex = /(\d+)/;
  return nodes?.sort((a, b) => {
    const numA = a.title.match(regex);
    const numB = b.title.match(regex);
    if (numA && numB) {
      const diff = parseInt(numA[0], 10) - parseInt(numB[0], 10);
      if (diff === 0) {
        // If numeric parts are equal, fall back to full string comparison
        return a.title.localeCompare(b.title);
      }
      return diff;
    }
    // If no numbers are found, sort by full strings
    return a.title.localeCompare(b.title);
  });
};

// Capitalize given string
export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Helper function to format the current date and time
export const getTimestamp = () => {
  const currentDate = new Date();
  const year = currentDate.getFullYear();
  const month = String(currentDate.getMonth() + 1).padStart(2, '0');
  const day = String(currentDate.getDate()).padStart(2, '0');
  const hours = String(currentDate.getHours()).padStart(2, '0');
  const minutes = String(currentDate.getMinutes()).padStart(2, '0');
  const seconds = String(currentDate.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day}_${hours}.${minutes}.${seconds}`;
};

// Helper function to get nested error message
export const getNestedValue = (obj: any, path: string): any => {
  return path
    .split('.')
    .reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj);
};
